import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../../components/common/Table";
import {
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useRolesInfinite } from "../../hooks/useRolesInfinite";
import { Role, RoleFormData } from "../../schemas/roleSchema";
import RoleDetails from "../../components/modals/RoleDetails";
import RoleForm from "../../components/forms/RoleForm";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { getChangedFields } from "../../utils/formUtils";

const RoleList: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const {
    roles,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createRole,
    updateRole,
    deleteRoles,
  } = useRolesInfinite({ search });
  const { canCreate, canDelete, canEdit, canView } =
    useAuthPermissions("roles");

  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (role: Role) => {
    if (
      window.confirm(`Are you sure you want to delete the role ${role?.name}?`)
    ) {
      await deleteRoles([role.id as string]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${ids.length} role(s)?`
      )
    ) {
      deleteRoles(ids);
      setRowSelection({});
    }
  };

  const handleView = (role: Role) => {
    setSelectedRole(role);
    setIsDetailsModalOpen(true);
  };

  const handleSubmit = async (data: RoleFormData) => {
    setIsSubmitting(true);

    try {
      if (selectedRole?.id) {
        // Only send changed fields for update
        const changedFields = getChangedFields(data, {
          name: selectedRole.name,
          description: selectedRole.description || "",
          permissionIds: selectedRole.permissions?.map((p) => p.id) || [],
        });
        await updateRole({
          id: selectedRole.id,
          role: changedFields,
        });
      } else {
        await createRole(data);
      }
    } finally {
      setIsFormModalOpen(false);
      setSelectedRole(undefined);
      setIsSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormModalOpen(false);
    setSelectedRole(undefined);
  };

  const handleDetailsClose = () => {
    setIsDetailsModalOpen(false);
    setSelectedRole(undefined);
  };

  const columns: ColumnDef<Role>[] = [
    { accessorKey: "name", header: "Role Name" },
    {
      accessorKey: "permissions",
      header: "Permissions",
      cell: ({ getValue }) => {
        const permissions = getValue() as any[];
        return (
          <div className="text-sm text-gray-600">
            {permissions?.length || 0} permission(s)
          </div>
        );
      },
    },
  ];

  const actions = [
    {
      label: "View",
      icon: <EyeIcon className="h-4 w-4" />,
      onClick: handleView,
      variant: "primary" as const,
      visible: canView,
    },
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
      visible: canEdit,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
      visible: canDelete,
    },
  ];

  return (
    <div className="flex flex-col px-6 py-2">
      {/* {canCreate && ( */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-800">Roles</h1>
        {canCreate && (
          <button
            onClick={() => setIsFormModalOpen(true)}
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
          >
            <PlusIcon className="h-6 w-6" />
          </button>
        )}
      </div>
      {/* )} */}

      {canView && (
        <Table
          columns={columns}
          data={roles as Role[]}
          isLoading={isLoading}
          actions={actions}
          searchable={true}
          pagination={true}
          maxHeight="67vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          onSearch={setSearch}
          selectable={canDelete}
          getRowId={(role) => role.id as string}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={
            canDelete
              ? [
                  {
                    label: "Delete Selected",
                    icon: <TrashIcon className="h-4 w-4" />,
                    onClick: handleBulkDelete,
                    variant: "danger" as const,
                  },
                ]
              : undefined
          }
        />
      )}

      {isFormModalOpen && (
        <RoleForm
          isOpen={isFormModalOpen}
          onCancel={handleFormCancel}
          initialData={selectedRole}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
        />
      )}

      {selectedRole && (
        <RoleDetails
          isOpen={isDetailsModalOpen}
          onClose={handleDetailsClose}
          data={selectedRole}
        />
      )}
    </div>
  );
};

export default RoleList;
